import { CommerceToolsCart, CustomLineItem, LineItem, PriceBreakdown } from '@/types/commercetools';
import { getCommissionLineItem, getDecorationLineItem } from '@/utility/lineItems/lineItemHelpers';

function getDirectDiscountForItem(item: CustomLineItem | LineItem | undefined): number {
  return item?.discountedPricePerQuantity && item?.discountedPricePerQuantity.length
    ? item.discountedPricePerQuantity.reduce(
        (result, discount) =>
          result +
          discount.quantity *
            discount.discountedPrice.includedDiscounts.reduce(
              (includesSum, includedDiscount) =>
                includesSum + includedDiscount.discountedAmount.centAmount,
              0
            ),
        0
      )
    : 0;
}

const getLineItemTaxedPrices = (lineItem: LineItem | CustomLineItem | undefined) => {
  return {
    inclVat: lineItem?.taxedPrice.totalGross.centAmount || 0,
    exclVat: lineItem?.taxedPrice.totalNet.centAmount || 0,
    taxRates:
      lineItem?.taxedPrice.taxPortions.map((portion) => ({
        name: portion.name,
        rate: portion.rate,
      })) || [],
  };
};

export const getLineItemTotalPrice = (
  lineItem: LineItem,
  cart: CommerceToolsCart
): PriceBreakdown => {
  const commissionItem = getCommissionLineItem(lineItem, cart);
  const decorationItem = getDecorationLineItem(lineItem, cart);
  const commissionPrice = commissionItem?.totalPrice.centAmount || 0;
  const decorationPrice = decorationItem?.totalPrice.centAmount || 0;

  const lineItemVolumeDiscount =
    (lineItem.custom.fields.sprdDiscountAmount?.centAmount || 0) * lineItem.quantity;
  const lineItemDirectDiscount = getDirectDiscountForItem(lineItem);

  const commissionVolumeDiscount =
    (commissionItem?.custom.fields.sprdDiscountAmount?.centAmount || 0) * lineItem.quantity;
  const commissionDirectDiscount = getDirectDiscountForItem(commissionItem);

  const decorationVolumeDiscount =
    (decorationItem?.custom.fields.sprdDiscountAmount?.centAmount || 0) * lineItem.quantity;
  const decorationDirectDiscount = getDirectDiscountForItem(decorationItem);

  const lineItemTaxedPrices = getLineItemTaxedPrices(lineItem);
  const commissionTaxedPrices = getLineItemTaxedPrices(commissionItem);
  const decorationTaxedPrices = getLineItemTaxedPrices(decorationItem);

  return {
    lineItemGroupInclVat: lineItemTaxedPrices.inclVat || 0,
    lineItemGroupExclVat: lineItemTaxedPrices.exclVat || 0,
    lineItemGroupTaxRates: lineItemTaxedPrices.taxRates || [],

    commissionInclVat: commissionTaxedPrices.inclVat || 0,
    commissionExclVat: commissionTaxedPrices.exclVat || 0,
    commissionTaxRates: commissionTaxedPrices.taxRates || [],

    decorationInclVat: decorationTaxedPrices.inclVat || 0,
    decorationExclVat: decorationTaxedPrices.exclVat || 0,
    decorationTaxRates: decorationTaxedPrices.taxRates || [],

    totalPrice:
      lineItem.totalPrice.centAmount +
      commissionPrice +
      decorationPrice +
      commissionVolumeDiscount +
      commissionDirectDiscount +
      decorationVolumeDiscount +
      decorationDirectDiscount +
      lineItemVolumeDiscount +
      lineItemDirectDiscount,
    totalDiscount:
      commissionVolumeDiscount +
      commissionDirectDiscount +
      decorationVolumeDiscount +
      decorationDirectDiscount +
      lineItemVolumeDiscount +
      lineItemDirectDiscount,
    totalDirectDiscount:
      commissionDirectDiscount + decorationDirectDiscount + lineItemDirectDiscount,
    totalVolumeDiscount:
      commissionVolumeDiscount + decorationVolumeDiscount + lineItemVolumeDiscount,
  };
};

export function addPriceBreakdown(first: PriceBreakdown, second: PriceBreakdown): PriceBreakdown {
  return {
    lineItemGroupInclVat: first.lineItemGroupInclVat + second.lineItemGroupInclVat,
    lineItemGroupExclVat: first.lineItemGroupExclVat + second.lineItemGroupExclVat,
    //Should be fine to take the first taxRate, as the other line items only vary in size and are added, so taxRates should stay the same. Tax amount can deducted from lineItemGroupInclVat - lineItemGroupExclVat
    lineItemGroupTaxRates: first.lineItemGroupTaxRates,
    commissionInclVat: first.commissionInclVat + second.commissionInclVat,
    commissionExclVat: first.commissionExclVat + second.commissionExclVat,
    commissionTaxRates: first.commissionTaxRates,
    decorationInclVat: first.decorationInclVat + second.decorationInclVat,
    decorationExclVat: first.decorationExclVat + second.decorationExclVat,
    decorationTaxRates: first.decorationTaxRates,
    totalPrice: first.totalPrice + second.totalPrice,
    totalDiscount: first.totalDiscount + second.totalDiscount,
    totalDirectDiscount: first.totalDirectDiscount + second.totalDirectDiscount,
    totalVolumeDiscount: first.totalVolumeDiscount + second.totalVolumeDiscount,
  };
}
